import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Reveal } from "../Reveal";

const CHAPTERS = [
  {
    n: "01",
    title: "Heritage",
    body: "Every Locka pattern begins in the archive — argyles lifted from 1920s fairways, deco fans from ballroom floors, paisleys traded along silk routes. We collect the past stitch by stitch.",
  },
  {
    n: "02",
    title: "Craft",
    body: "Two-hundred-needle knitting. Hand-linked toes you cannot feel. Combed cotton, silk blends and a single golden thread running through every pair — our signature, woven in.",
  },
  {
    n: "03",
    title: "Flair",
    body: "Then we break the rules. Heritage motifs recut in burgundy and gold, scaled for sneakers as much as brogues. Vintage inspiration, modern craftsmanship — worn with flair.",
  },
];

export const Story = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [60, -60]);

  return (
    <section id="story" ref={ref} className="relative overflow-hidden bg-[#0F1830] py-28 text-[#F2ECDF] md:py-44" data-testid="story-section">
      <span aria-hidden="true" className="text-outline-cream pointer-events-none absolute right-0 top-10 select-none font-serif text-[16vw] font-bold leading-none">
        Atelier
      </span>
      <div className="relative mx-auto grid max-w-[1500px] gap-16 px-6 md:px-10 lg:grid-cols-12">
        <div className="lg:col-span-6">
          <Reveal>
            <p className="mb-4 flex items-center gap-3 text-[0.68rem] font-bold uppercase tracking-[0.34em] text-gold">
              <span className="h-px w-10 bg-gold" /> The Locka Manifesto
            </p>
            <h2 className="font-serif text-4xl leading-[1.06] tracking-tight sm:text-5xl lg:text-6xl" data-testid="story-headline">
              Crafted With Heritage,
              <br />
              <em className="text-gold">Worn With Flair</em>
            </h2>
          </Reveal>
          <div className="mt-16 space-y-0">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.12}>
                <div className="group flex gap-8 border-t border-white/12 py-9 transition-colors duration-500 hover:border-gold/50" data-testid={`story-chapter-${c.n}`}>
                  <span className="font-serif text-4xl italic text-gold/70 transition-colors duration-500 group-hover:text-gold">{c.n}</span>
                  <div>
                    <h3 className="font-serif text-2xl text-[#F2ECDF] md:text-3xl">{c.title}</h3>
                    <p className="mt-3 max-w-md leading-relaxed text-[#F2ECDF]/60">{c.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
        <div className="relative lg:col-span-6">
          <motion.div style={{ y: yImg }} className="sticky top-28">
            <Reveal delay={0.2}>
              <div className="relative ml-auto aspect-[4/5] max-w-md overflow-hidden border border-gold/25 lg:max-w-lg">
                <img src="/images/story-atelier.jpg" alt="Artisan hands linking a Locka sock in the atelier" className="h-full w-full object-cover" data-testid="story-image" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                <p className="absolute bottom-5 left-5 text-[0.62rem] font-bold uppercase tracking-[0.3em] text-gold">The Atelier · Como, Italy</p>
              </div>
            </Reveal>
            <Reveal delay={0.35} className="mt-6 flex justify-end">
              <p className="max-w-xs text-right text-sm italic leading-relaxed text-[#F2ECDF]/55">
                “A sock is the first thing you put on and the last thing the world notices — we make sure it is unforgettable.”
              </p>
            </Reveal>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
